import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { asArray, asStringArray } from "@/lib/safeData";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import TrustScore from "@/components/TrustScore";
import {
  BadgeCheck, AlertTriangle, MapPin, Star, ArrowLeft,
  Bookmark, BookmarkCheck, MessageSquarePlus, ShieldCheck, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const RATING_AXES = [
  { key: "transparency", label: "Transparency" },
  { key: "accuracy", label: "Accuracy of calls / advice" },
  { key: "support", label: "Customer support" },
  { key: "refund", label: "Refund behaviour" },
  { key: "professionalism", label: "Professionalism" },
];

const Stars = ({ value, onChange, testId }) => (
  <div className="flex items-center gap-1" data-testid={testId}>
    {[1, 2, 3, 4, 5].map((v) => (
      <button
        type="button"
        key={v}
        onClick={() => onChange(v)}
        className="text-2xl transition-transform hover:scale-110"
        aria-label={`${v} stars`}
      >
        <Star className={`h-6 w-6 ${v <= value ? "fill-[#D4AF37] text-[#D4AF37]" : "text-white/25"}`} />
      </button>
    ))}
  </div>
);

const TrustBreakdownBar = ({ label, value, max = 1, format }) => (
  <div>
    <div className="flex items-center justify-between text-xs">
      <span className="text-white/65">{label}</span>
      <span className="font-mono-alt text-[#D4AF37]">{format ? format(value) : value}</span>
    </div>
    <div className="mt-1.5 h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#FFD66B]" style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
    </div>
  </div>
);

const MentorDetail = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [m, setM] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);

  // review form state
  const [ratings, setRatings] = useState({ transparency: 0, accuracy: 0, support: 0, refund: 0, professionalism: 0 });
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [anon, setAnon] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mres, rres] = await Promise.all([
        api.get(`/mentors/${slug}`),
        api.get(`/mentors/${slug}/reviews`),
      ]);
      setM(mres.data);
      setReviews(asArray(rres.data));
      if (user && mres.data?.id) {
        try {
          const saved = await api.get("/me/saved-mentors");
          setIsSaved(asArray(saved.data).some((x) => x.id === mres.data.id));
        } catch {}
      }
    } catch {
      setM(null);
    } finally {
      setLoading(false);
    }
  }, [slug, user]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const toggleSave = async () => {
    if (!user) { nav("/login", { state: { from: `/mentors/${slug}` } }); return; }
    try {
      if (isSaved) {
        await api.delete(`/me/saved-mentors/${m.id}`);
        setIsSaved(false);
        toast.success("Removed from saved");
      } else {
        await api.post(`/me/saved-mentors/${m.id}`);
        setIsSaved(true);
        toast.success("Saved to your dashboard");
      }
    } catch {
      toast.error("Action failed");
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { nav("/login", { state: { from: `/mentors/${slug}` } }); return; }
    if (Object.values(ratings).some((v) => v < 1)) {
      toast.error("Please rate all five categories");
      return;
    }
    if (body.length < 20) {
      toast.error("Review must be at least 20 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/mentors/${m.id}/reviews`, {
        ratings, title: title || "Untitled review", body, is_anonymous: anon, evidence_paths: [],
      });
      toast.success("Review submitted");
      setReviewOpen(false);
      setRatings({ transparency: 0, accuracy: 0, support: 0, refund: 0, professionalism: 0 });
      setTitle(""); setBody(""); setAnon(false);
      loadAll();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-32 text-center text-white/50">Loading…</div>;
  if (!m) return (
    <div className="py-32 text-center">
      <div className="text-white/60">Mentor not found</div>
      <Link to="/mentors"><Button variant="outline" className="mt-4 border-white/20">Back to mentors</Button></Link>
    </div>
  );

  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";
  const trust = m.trust || {};
  const trustScore = Number.isFinite(Number(trust.score)) ? Number(trust.score).toFixed(1) : "0.0";
  const bd = trust.breakdown || {
    avg_rating: 0,
    response_rate: 0,
    verified_reviews_share: 0,
    verified_status: false,
    complaint_ratio: 0,
  };
  const reviewList = asArray(reviews);

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-12" data-testid="mentor-detail">
      <Link to="/mentors" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> All mentors
      </Link>

      <div className="mt-6 surface-card p-6 md:p-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 flex-wrap">
              {m.is_verified && <Badge className="bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 gap-1"><BadgeCheck className="h-3 w-3" /> Verified</Badge>}
              {m.is_flagged && <Badge className="bg-[#FF3B30]/15 text-[#FF6A60] border border-[#FF3B30]/30 gap-1"><AlertTriangle className="h-3 w-3" /> Flagged</Badge>}
              <span className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">{m.category}</span>
            </div>
            <h1 className="mt-3 font-display text-3xl md:text-4xl font-black tracking-tight">{m.name}</h1>
            <div className="mt-1 text-sm text-white/55">
              {m.company}{m.state && <> · <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {m.state}</span></>}
            </div>
            <p className="mt-5 text-white/80 leading-relaxed">{m.bio}</p>

            {asStringArray(m.claims).length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">Mentor's Claims</div>
                <ul className="mt-2 space-y-1.5">
                  {asStringArray(m.claims).map((c) => (
                    <li key={c} className="text-sm text-white/80 flex items-start gap-2"><span className="text-[#D4AF37]">•</span> {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {asStringArray(m.services).length > 0 && (
              <div className="mt-6">
                <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-mono-alt">Services</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {asStringArray(m.services).map((s) => (
                    <span key={s} className="text-xs text-white/80 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{s}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => user ? setReviewOpen(true) : nav("/login", { state: { from: `/mentors/${slug}` } })} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2" data-testid="open-review-btn">
                <MessageSquarePlus className="h-4 w-4" /> Write a review
              </Button>
              <Button onClick={toggleSave} variant="outline" className="border-white/20 gap-2" data-testid="save-mentor-btn">
                {isSaved ? <><BookmarkCheck className="h-4 w-4 text-[#D4AF37]" /> Saved</> : <><Bookmark className="h-4 w-4" /> Save</>}
              </Button>
            </div>
          </div>

          <aside className="lg:col-span-4 space-y-5">
            <div className="surface-card p-5">
              <div className="text-[11px] uppercase tracking-widest text-[#D4AF37] font-mono-alt inline-flex items-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" /> CHINTAMAT TRUST SCORE™
              </div>
              <div className="mt-3 flex items-baseline gap-2">
                <div className="font-display text-5xl font-black text-[#D4AF37] tabular-nums">{trustScore}</div>
                <div className="text-white/50">/10</div>
              </div>
              <div className="mt-1 text-xs text-white/60">{trust.count ?? 0} reviews · ⭐ {trust.avg_rating ?? 0}/5</div>

              <div className="mt-5 space-y-3.5">
                <TrustBreakdownBar label="Avg user rating" value={bd.avg_rating} max={5} format={(v) => `${v}/5`} />
                <TrustBreakdownBar label="Response rate" value={bd.response_rate} max={1} format={(v) => `${Math.round(v * 100)}%`} />
                <TrustBreakdownBar label="Verified reviews share" value={bd.verified_reviews_share} max={1} format={(v) => `${Math.round(v * 100)}%`} />
                <div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/65">Verification status</span>
                    <span className="font-mono-alt text-[#D4AF37]">{bd.verified_status ? "Verified" : "Unverified"}</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-white/65">Complaint ratio</span>
                    <span className={`font-mono-alt ${bd.complaint_ratio > 0.2 ? "text-[#FF6A60]" : "text-[#D4AF37]"}`}>{Math.round(bd.complaint_ratio * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-[11px] text-white/45 leading-relaxed">
              Trust Score is calculated from user reviews, complaint ratio, response rate, and verification status. It is not a financial recommendation.
            </div>
          </aside>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-12">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-display text-2xl font-bold">Reviews ({reviewList.length})</h2>
          <Button onClick={() => user ? setReviewOpen(true) : nav("/login")} className="bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="bottom-review-btn">
            Write a review
          </Button>
        </div>

        <div className="mt-6 space-y-4" data-testid="mentor-reviews-list">
          {reviewList.length === 0 && (
            <div className="surface-card p-10 text-center text-white/55">No reviews yet. Be the first to share your experience.</div>
          )}
          {reviewList.map((r, i) => (
            <div key={r.id ?? `review-${i}`} className="surface-card p-5" data-testid={`review-${r.id ?? i}`}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="font-display text-base font-bold flex items-center gap-2">
                    {r.title}
                    {r.is_verified && <Badge className="bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30 text-[9px] uppercase">Verified review</Badge>}
                  </div>
                  <div className="mt-0.5 text-xs text-white/50 font-mono-alt">
                    by {r.is_anonymous ? "Anonymous" : r.user_name} · {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                </div>
                <div className="inline-flex items-center gap-1 text-[#D4AF37] font-mono-alt font-bold"><Star className="h-4 w-4 fill-current" /> {r.avg_rating}/5</div>
              </div>
              <p className="mt-3 text-sm text-white/80 leading-relaxed">{r.body}</p>
              <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px]">
                {RATING_AXES.map((ax) => (
                  <div key={ax.key} className="flex items-center justify-between">
                    <span className="text-white/55">{ax.label}</span>
                    <span className="font-mono-alt text-[#D4AF37]">{r.ratings?.[ax.key] || "-"}/5</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Review Modal */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Review {m.name}</DialogTitle>
            <DialogDescription className="text-white/60 text-sm">
              Be specific and factual. Verified reviews with evidence carry more weight.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitReview} className="space-y-5">
            {RATING_AXES.map((ax) => (
              <div key={ax.key} className="flex items-center justify-between gap-4">
                <Label className="text-sm text-white/85">{ax.label}</Label>
                <Stars value={ratings[ax.key]} onChange={(v) => setRatings((r) => ({ ...r, [ax.key]: v }))} testId={`stars-${ax.key}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} className={`mt-2 ${fieldCls}`} placeholder="One-line summary" data-testid="review-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">Your review</Label>
              <Textarea rows={5} value={body} onChange={(e) => setBody(e.target.value)} className={`mt-2 resize-none ${fieldCls}`} placeholder="What was promised, what you experienced, dates, amounts. Min 20 chars." data-testid="review-body" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={anon} onCheckedChange={setAnon} className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" />
              <span className="text-sm text-white/80">Post as anonymous</span>
            </label>
            <Button type="submit" disabled={submitting} className="w-full h-11 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold" data-testid="submit-review-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publish review"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MentorDetail;
